import React, { memo, ReactNode } from 'react';

import Button from '@plasma/ui.general.button/dist/button';
import Input from '@plasma/ui.input.input';
import Select from '@plasma/ui.input.select';
import Modal from '@plasma/ui.layout.modal/dist/modal';
import ThemeProvider from '@plasma/ui.themes.theme-provider';
import { ThemeOverride } from '@plasma/ui.utils.types';

import LoadingComponentScalable from '../../components/Loding-component/LoadingComponentMini';
import TopBarThemed from '../../components/top-bar-themed';
import themes from './themes';
import useUploadExcelHook from './upload-excel.hook';
import { NormalPlasmaButton, UploadExcelStyles } from './upload-excel.styles';
import * as styled from './upload-excel.styles';

const { Option } = Select;

export interface UploadExcelProps {
  children?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

type Props = UploadExcelProps & ThemeOverride;

function UploadExcel(props: Props) {
  const { children, className, style, size, themeMode } = props;

  const {
    t,
    handleExtractData,
    sheet,
    handleApiExtract,
    handleCancel,
    setSheet,
    pass,
    setPass,
    sheetOptions,
    sheetNamesLoading,
    sheetNamesError,
    passwordRequired,
    handleVerifyPassword,
    extractMessage,
    responseExcel,
    proce,
    process,
    extractSuccess,
    showPassword,
    toggleButton,
    progressLog,
    estimatedSecondsRemaining,
    estimatedRowsPerSecond,
    elapsedSeconds,
    logContainerRef,
    fileInputRef,
    filePath,
    responseSet,
    uploading,
    uploadError,
    resetConfirmVisible,
    handleButtonClick,
    handleFileChange,
    handleResetClick,
    confirmResetInterrupt,
    cancelResetInterrupt,
    handleUpload,
    downloading,
    downloadError,
    handleDownloadExcel,
    STAGE_GROUPS,
    getStageBarInfo,
    formatDuration,
    formatEstimatedTimeRemaining,
    formatRowsPerSecond,
    activeStep,
  } = useUploadExcelHook(props);

  // Defensive on purpose: if progressLog is ever missing/malformed (backend not sending the
  // expected shape, connection dropped mid-frame, etc.) this must never throw during render -
  // fall back to the plain "Extracting Data..." text instead of taking the whole page down.
  const renderProgressLog = (): React.ReactNode => {
    try {
      if (!Array.isArray(progressLog) || progressLog.length === 0) {
        return null;
      }
      return progressLog.map((entry, index) => (
        <div key={index}>
          <span style={{ opacity: 0.6 }}>[{entry?.stage ?? '...'}]</span> {entry?.message ?? ''}
          {entry?.current != null && entry?.total ? ` (${entry.current}/${entry.total})` : ''}
        </div>
      ));
    } catch (error) {
      console.log('Failed to render progress log', error);
      return null;
    }
  };

  return (
    <ThemeProvider themes={themes} selectedSize={size} selectedMode={themeMode}>
      <styled.UploadExcel className={`upload-excel ${className ?? ''}`.trim()} style={style}>
        <TopBarThemed topHeading={t('upload-plant-config')} />
        <styled.PageSummary>{t('page-summary')}</styled.PageSummary>
        <styled.MainLayout>
          <styled.LeftPanel>
            <styled.UploadTemplateContainer>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  padding: '20px',
                  gap: '1rem',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept=".xlsx, .xls, .xlm, .xlsm"
                  onChange={handleFileChange}
                />

                <Input
                  type="text"
                  value={filePath}
                  style={{ width: '400px', flex: '1 1 auto' }}
                  placeholder={t('select-excel-file')}
                  readOnly
                />
                <Button
                  title={t('browse') || 'Browse'}
                  type={'secondary'}
                  htmlType={'button'}
                  onClick={handleButtonClick}
                />
                <Button title={t('reset') || 'Reset'} type={'danger'} htmlType={'button'} onClick={handleResetClick} />
              </div>

              {responseSet.length === 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '10px',
                    gap: '10px',
                  }}
                >
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <NormalPlasmaButton
                      title={t('upload-excel')}
                      type={'primary'}
                      htmlType={'button'}
                      disabled={!filePath || uploading}
                      onClick={() => {
                        handleUpload();
                      }}
                    />
                    {/* The Excel file already uploaded for the company (AppFile/downloadExcelFile) */}
                    <NormalPlasmaButton
                      title={downloading ? t('downloading-excel') : t('download-excel')}
                      htmlType={'button'}
                      type={'primary'}
                      disabled={downloading || uploading}
                      onClick={() => {
                        handleDownloadExcel();
                      }}
                    />
                    {downloading && <LoadingComponentScalable borderWidth={4} containerSize={40} />}
                  </div>
                  {downloadError && !downloading && <styled.ErrorMessage>{downloadError}</styled.ErrorMessage>}
                  {uploading && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <LoadingComponentScalable borderWidth={4} containerSize={40} />
                      <span style={{ color: '#267DAC', fontWeight: 600 }}>Uploading... Please wait</span>
                    </div>
                  )}
                  {uploadError && !uploading && <styled.ErrorMessage>{uploadError}</styled.ErrorMessage>}
                </div>
              ) : (
                <div>
                  {process && !extractMessage && (
                    <styled.SuccessMessage>{filePath} Uploaded Successfully</styled.SuccessMessage>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      width: '100%',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '10px',
                    }}
                  >
                    <Button
                      type={'primary'}
                      htmlType={'button'}
                      title={t('extract-data')}
                      disabled={!process}
                      onClick={() => {
                        handleExtractData();
                      }}
                    />
                  </div>
                </div>
              )}

              {!process && (
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '20px', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                    <LoadingComponentScalable borderWidth={4} containerSize={50} />
                    <span style={{ color: '#267DAC', fontWeight: 600 }}>
                      Extracting Data... Please wait
                      <span style={{ fontWeight: 400, opacity: 0.75 }}>
                        {' '}
                        ({formatDuration(elapsedSeconds)} elapsed)
                      </span>
                    </span>
                  </div>

                  {STAGE_GROUPS.map(({ key, label, stages }) => {
                    const { percent, message, started, active } = getStageBarInfo(stages);
                    const isComplete = percent === 100;
                    const rateText =
                      key === 'insert' && active && !isComplete ? formatRowsPerSecond(estimatedRowsPerSecond) : null;
                    const etaText =
                      key === 'insert' && active && !isComplete
                        ? formatEstimatedTimeRemaining(estimatedSecondsRemaining)
                        : null;
                    return (
                      <div
                        key={key}
                        style={{ display: 'flex', flexDirection: 'column', gap: '4px', opacity: started ? 1 : 0.4 }}
                      >
                        <div
                          style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#555' }}
                        >
                          <span style={{ fontWeight: active ? 700 : 500 }}>
                            {isComplete ? '✅ ' : active ? '👉 ' : ''}
                            {label}
                          </span>
                          <span>
                            {started ? (percent != null ? `${percent}%` : message) : ''}
                            {(rateText || etaText) && (
                              <span style={{ marginLeft: '8px', fontWeight: 400, opacity: 0.75 }}>
                                ({[rateText, etaText].filter(Boolean).join(' · ')})
                              </span>
                            )}
                          </span>
                        </div>
                        <div
                          style={{
                            width: '100%',
                            height: '10px',
                            borderRadius: '5px',
                            background: '#e6e6e6',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${started ? percent ?? 8 : 0}%`,
                              height: '100%',
                              background: isComplete ? '#28a745' : '#267DAC',
                              transition: 'width 0.3s ease',
                              animation:
                                active && percent === null
                                  ? 'upload-excel-progress-indeterminate 1.2s ease-in-out infinite alternate'
                                  : undefined,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  <style>{`
                  @keyframes upload-excel-progress-indeterminate {
                    from { margin-left: 0%; }
                    to { margin-left: 85%; }
                  }
                `}</style>

                  {/* If no log lines are available at all (backend not sending progress, connection
                    not yet established, or the stream broke before anything arrived), fall back to
                    the plain loading text above instead of showing an empty/broken log panel. */}
                  {Array.isArray(progressLog) && progressLog.length > 0 && (
                    <div
                      ref={logContainerRef}
                      style={{
                        width: '100%',
                        maxHeight: '220px',
                        overflowY: 'auto',
                        background: '#0d1117',
                        color: '#f0f3f6',
                        fontFamily: 'Consolas, Monaco, monospace',
                        fontSize: '12px',
                        lineHeight: 1.6,
                        borderRadius: '6px',
                        padding: '10px 12px',
                      }}
                    >
                      {renderProgressLog()}
                    </div>
                  )}
                </div>
              )}

              {extractMessage &&
                (!extractSuccess ? (
                  <styled.ErrorMessage>
                    {responseExcel} (took {formatDuration(elapsedSeconds)})
                  </styled.ErrorMessage>
                ) : (
                  <styled.SuccessMessage>
                    {responseExcel} (took {formatDuration(elapsedSeconds)})
                  </styled.SuccessMessage>
                ))}

              {proce && (
                <Modal
                  title={t('extract-data') || 'Extract Data Details'}
                  visible={proce}
                  onCancel={() => handleCancel()}
                  type="complex"
                  footer={
                    <div
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
                    >
                      <Button
                        title={t('reset') || 'Reset'}
                        type={'danger'}
                        htmlType={'button'}
                        onClick={handleResetClick}
                      />
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                          title={t('cancel') || 'CANCEL'}
                          type={'secondary'}
                          htmlType={'button'}
                          onClick={() => handleCancel()}
                        />
                        <Button
                          title={'OK'}
                          type={'primary'}
                          htmlType={'button'}
                          disabled={sheetNamesLoading || passwordRequired || !sheet}
                          onClick={() => handleApiExtract()}
                        />
                      </div>
                    </div>
                  }
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', padding: '10px 0' }}>
                    <styled.ErrorMessage style={{ margin: 0 }}>{t('delete-existing-records')}</styled.ErrorMessage>

                    {passwordRequired ? (
                      <>
                        <styled.ErrorMessage style={{ margin: 0 }}>
                          {sheetNamesError ||
                            t('password-protected-message') ||
                            'This workbook is password protected. Please provide the password.'}
                        </styled.ErrorMessage>
                        <styled.InputGroup>
                          <label id="password">{t('password')}</label>
                          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '5px' }}>
                            <Input
                              id="password"
                              type={showPassword ? 'text' : 'password'}
                              value={pass}
                              onChange={(e) => setPass(e.target.value)}
                              size={'medium'}
                              autoComplete="off"
                            />
                            <Button
                              type={'primary'}
                              htmlType={'button'}
                              onClick={() => toggleButton()}
                              prefixIcon={{ name: showPassword ? 'visibility' : 'visibility_off' }}
                            />
                          </div>
                        </styled.InputGroup>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            title={sheetNamesLoading ? t('checking') || 'Checking...' : t('unlock') || 'Unlock'}
                            type={'primary'}
                            htmlType={'button'}
                            disabled={!pass || sheetNamesLoading}
                            onClick={() => handleVerifyPassword()}
                          />
                        </div>
                      </>
                    ) : sheetNamesLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <LoadingComponentScalable borderWidth={3} containerSize={24} />
                        <span>{t('loading-sheet-names') || 'Reading sheet names from the workbook...'}</span>
                      </div>
                    ) : sheetNamesError ? (
                      <styled.ErrorMessage style={{ margin: 0 }}>{sheetNamesError}</styled.ErrorMessage>
                    ) : (
                      <styled.InputGroup>
                        <label id="sheetname">{t('sheet-name')}</label>
                        <Select
                          id="sheetname"
                          style={{ width: '220px' }}
                          placeholder={t('select-sheet') || 'Select a sheet'}
                          value={sheet || undefined}
                          onChange={(value) => setSheet(value)}
                          dropdownStyle={{ maxHeight: 200, overflow: 'auto' }}
                        >
                          {sheetOptions.map((name) => (
                            <Option key={name} value={name}>
                              {name}
                            </Option>
                          ))}
                        </Select>
                      </styled.InputGroup>
                    )}
                  </div>
                </Modal>
              )}

              {resetConfirmVisible && (
                <Modal
                  title={t('reset-confirm-title')}
                  okText={t('reset-confirm-ok')}
                  cancelText={t('reset-confirm-cancel')}
                  visible={resetConfirmVisible}
                  onCancel={cancelResetInterrupt}
                  onOk={confirmResetInterrupt}
                  type="confirm"
                >
                  {t('reset-confirm-message')}
                </Modal>
              )}
            </styled.UploadTemplateContainer>
          </styled.LeftPanel>

          <styled.RightPanel>
            <div style={{ ...UploadExcelStyles.container, flexDirection: 'column' }}>
              <h2 style={UploadExcelStyles.heading}>{t('steps-to-follow')}</h2>
              <ul style={UploadExcelStyles.list}>
                {[
                  t('ensure-file-format'),
                  t('click-upload-button'),
                  t('extract-button-appears'),
                  t('popup-enter-details'),
                  t('incorrect-details'),
                ].map((stepText, index) => {
                  const isCompleted = activeStep > index;
                  const isActive = activeStep === index;

                  const stepStyle = {
                    ...UploadExcelStyles.listItem,
                    color: isActive ? '#267DAC' : isCompleted ? '#28a745' : '#888',
                    fontWeight: isActive ? 700 : isCompleted ? 600 : 400,
                    opacity: isActive || isCompleted ? 1 : 0.5,
                    transform: isActive ? 'scale(1.02) translateX(5px)' : 'none',
                    boxShadow: isActive ? '0 4px 12px rgba(38,125,172,0.15)' : 'none',
                    border: isActive
                      ? '1px solid rgba(38,125,172,0.5)'
                      : isCompleted
                        ? '1px solid rgba(40,167,69,0.3)'
                        : '1px solid #f0f0f0',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  };

                  let icon = '⚪';
                  if (isCompleted) icon = '✅';
                  if (isActive) icon = '👉';

                  return (
                    <li key={index} style={stepStyle}>
                      <span>{icon}</span>
                      <span>{stepText}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </styled.RightPanel>
        </styled.MainLayout>
      </styled.UploadExcel>
    </ThemeProvider>
  );
}

export default memo(UploadExcel) as unknown as typeof UploadExcel;
